import React, { useState, useEffect } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { track } from '@lf/analytics-events';
import lfApi from '@lf/api-client/lfApi';
import { LoadingButton } from '@lf/ui-primitives';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY);

const PLANS = {
  monthly_standard: {
    label: 'Student Plan',
    price: '$3.99/mo',
    seats: '1 student',
    features: [
      'AI-built study plans',
      'Curated videos, no ads',
      'Progress tracking',
    ],
  },
  monthly_family: {
    label: 'Family Plan',
    price: '$7.99/mo',
    seats: 'Up to 4 students',
    badge: 'BEST',
    features: [
      'Everything in Student Plan',
      'Up to 4 student profiles',
      'Manage all your kids in one place',
    ],
  },
};

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function PlanSelectorInner({
  defaultPlan = 'monthly_family',
  context = 'trial',
  surface,
  onError,
}) {
  const [selectedPlan, setSelectedPlan] = useState(defaultPlan);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    track('plan_selector_view', {
      surface: surface || context,
      default_plan: defaultPlan,
    });
  }, [surface, context, defaultPlan]);

  const handleSelectPlan = (priceType) => {
    if (isLoading) return;
    track('plan_select', {
      plan: priceType === 'monthly_family' ? 'family' : 'student',
      price_type: priceType,
      surface: surface || context,
      previous_plan: selectedPlan,
    });
    setSelectedPlan(priceType);
    setError('');
  };

  const handleCheckout = async () => {
    if (isLoading) return;
    setIsLoading(true);
    setError('');

    const plan = selectedPlan === 'monthly_family' ? 'family' : 'student';
    track('checkout_start', {
      plan,
      price_type: selectedPlan,
      surface: surface || context,
      context,
    });

    try {
      const stripe = await stripePromise;
      const response = await lfApi.post('/subscription/checkout_session', {
        price_type: selectedPlan,
        quantity: 1,
      });
      const session = response.data;

      const delay = 5000;
      setTimeout(async () => {
        track('checkout_redirect', {
          plan,
          price_type: selectedPlan,
          surface: surface || context,
        });
        const result = await stripe.redirectToCheckout({
          sessionId: session.session_id,
        });
        if (result.error) {
          console.error(result.error.message);
        }
      }, delay);

      await sleep(delay);
    } catch (err) {
      const errorCode = err?.response?.status || 'unknown';
      track('checkout_error', {
        plan,
        price_type: selectedPlan,
        surface: surface || context,
        error_code: String(errorCode),
      });
      setError('Something went wrong. Please try again.');
      if (onError) onError(err);
      setIsLoading(false);
    }
  };

  const plan = PLANS[selectedPlan];
  // Learnforest owns the free trial app-side (it starts at signup, no card),
  // so checkout is always a plain paid subscribe — never "start free trial."
  // See docs/design-subscription-trial-ownership.md.
  const ctaLabel = 'Subscribe';

  return (
    <div>
      <div className="text-base font-medium mb-4 text-darkrock">
        Which plan fits your family?
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        {Object.entries(PLANS).map(([priceType, info]) => {
          const selected = selectedPlan === priceType;
          return (
            <button
              key={priceType}
              type="button"
              aria-pressed={selected}
              aria-label={`${info.label} ${info.price} ${info.seats}`}
              disabled={isLoading}
              onClick={() => handleSelectPlan(priceType)}
              className={`flex-1 flex flex-col rounded-xl border-2 p-5 text-left transition-colors
                ${selected
                  ? 'border-primary-600 bg-primary-50'
                  : 'border-gray-200 hover:border-primary-300 bg-white'}
                ${isLoading ? 'pointer-events-none opacity-75' : ''}`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="font-semibold text-sm text-darkrock">{info.label}</span>
                {info.badge && (
                  <span className="text-xs bg-primary-100 text-primary-700 rounded px-1.5 py-0.5 font-medium">
                    {info.badge}
                  </span>
                )}
              </div>
              <div className="text-2xl font-bold text-darkrock">{info.price}</div>
              <div className="text-xs text-darkrock/60 mb-3">{info.seats}</div>
              <ul className="space-y-1">
                {info.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-1.5 text-xs text-darkrock/70">
                    <span className="text-primary-600">✓</span>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </button>
          );
        })}
      </div>

      <LoadingButton
        text={ctaLabel}
        aria-label={`${ctaLabel} — ${plan.label}`}
        aria-busy={isLoading}
        isLoading={isLoading}
        onClick={handleCheckout}
        className="w-full bg-primary-600 text-white py-3 rounded-xl font-semibold"
      />

      {error && (
        <div role="alert" className="text-red-500 text-sm mt-2">
          {error}
        </div>
      )}
    </div>
  );
}

function PlanSelector(props) {
  return (
    <Elements stripe={stripePromise}>
      <PlanSelectorInner {...props} />
    </Elements>
  );
}

export default PlanSelector;
